import { BACKEND_URI } from "@/constants/config"

import { bytesToDataUri, mimeFromFilename, resolveRefToFilename } from "./session-image-refs"

type ResolveContext = {
  sessionId: string
  filenames: string[]
  fetchBytes: (url: string) => Promise<ArrayBuffer>
  /** Overrides the fetch URL per filename (e.g. document-content for saved artifacts).
   * When omitted, the session-content endpoint is used. */
  buildUrl?: (filename: string) => string
  /** Fail the whole operation if a referenced known image cannot be fetched. */
  strict?: boolean
}

// Each alternative captures one URL token and ends right before its closing
// quote, so the replacement can swap the match's suffix. Quoted forms come first
// and read up to their closing quote on the same line, so filenames with
// spaces, "(" or "&" stay whole (#3595):
//   src="X" / src='X', markdown ![…](<X>), and CSS url(…) with a plain,
//   JS-escaped (\" \') or HTML-entity (&quot; &#39;) quote — in a <style>
//   block, a style="" attribute or a React inline style.
// Unquoted forms keep stopping at whitespace: markdown ![…](X) and url(X).
const REF_PATTERNS = [
  /src\s*=\s*"([^"\n]+)(?=")/,
  /src\s*=\s*'([^'\n]+)(?=')/,
  /!\[[^\]]*\]\(\s*<([^>\n]+)(?=>)/,
  /url\(\s*\\"((?:(?!\\")[^\n])+)(?=\\")/,
  /url\(\s*\\'((?:(?!\\')[^\n])+)(?=\\')/,
  /url\(\s*&quot;((?:(?!&quot;)[^\n])+)(?=&quot;)/,
  /url\(\s*&#0*39;((?:(?!&#0*39;)[^\n])+)(?=&#0*39;)/,
  /url\(\s*"([^"\n]+)(?=")/,
  /url\(\s*'([^'\n]+)(?=')/,
  /!\[[^\]]*\]\(([^"')\s<]+)/,
  /url\(\s*([^"'()\s\\&]+)/,
]
const REF_REGEX = new RegExp(REF_PATTERNS.map((pattern) => pattern.source).join("|"), "g")

/** The URL token a REF_REGEX match captured (exactly one group matches). */
function matchedRef(groups: (string | undefined)[]): string {
  return groups.find((group) => group !== undefined) ?? ""
}

/**
 * Replaces session-image references (sandbox:/mnt/data/…, /session-content/…, or
 * a bare known filename) in artifact HTML/markdown, including CSS url(…), with
 * inline data: URIs. Each distinct file is fetched once via the authenticated endpoint (in the parent
 * context, which holds the MSAL token) and base64-inlined so the sandbox iframe
 * — restricted to img-src data: — can display it. Unknown/external refs and refs
 * whose fetch fails are left untouched.
 */
export async function resolveArtifactImages(content: string, ctx: ResolveContext): Promise<string> {
  // Need either a session (default endpoint) or an explicit URL builder (document
  // endpoint), plus at least one known filename.
  if ((!ctx.sessionId && !ctx.buildUrl) || ctx.filenames.length === 0) return content

  // Collect the distinct filenames actually referenced in the content.
  const referenced = new Set<string>()
  for (const match of content.matchAll(REF_REGEX)) {
    const filename = resolveRefToFilename(matchedRef(match.slice(1)), ctx.filenames)
    if (filename) referenced.add(filename)
  }
  if (referenced.size === 0) return content

  // Fetch each referenced file once → data URI (skip files whose fetch fails).
  const dataUriByFilename = new Map<string, string>()
  await Promise.all(
    [...referenced].map(async (filename) => {
      try {
        const url = ctx.buildUrl
          ? ctx.buildUrl(filename)
          : `${BACKEND_URI}/session-content/${ctx.sessionId}/${encodeURIComponent(filename)}`
        const bytes = await ctx.fetchBytes(url)
        dataUriByFilename.set(filename, bytesToDataUri(bytes, mimeFromFilename(filename)))
      } catch (error) {
        if (ctx.strict) throw error
        // leave the reference untouched on failure
      }
    })
  )
  if (dataUriByFilename.size === 0) return content

  // Replace every matched ref that resolved to a fetched file.
  return content.replace(REF_REGEX, (whole: string, ...args: unknown[]) => {
    const ref = matchedRef(args.slice(0, REF_PATTERNS.length) as (string | undefined)[])
    const filename = resolveRefToFilename(ref, ctx.filenames)
    const dataUri = filename ? dataUriByFilename.get(filename) : undefined
    if (!dataUri) return whole
    // The ref always ends the match; swap that suffix rather than the first
    // occurrence, which may be alt text like ![chart_1.jpg](chart_1.jpg).
    return whole.slice(0, whole.length - ref.length) + dataUri
  })
}
